import React, { useState } from 'react';

// US Citizenship Step 6: Document Quality Enhancement
export const CitizenshipStep6DocumentEnhancement = ({ formData, setFormData, onNext, onBack }) => {
  const [enhancementSuggestions, setEnhancementSuggestions] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const documentEnhancements = [
    {
      category: 'Tax Documentation',
      priority: 'High',
      suggestions: [
        'Obtain IRS tax transcripts for all years as permanent resident',
        'Include W-2 forms and 1099s for employment verification',
        'Get certified copies of tax returns filed',
        'Include payment records if any taxes were owed'
      ]
    },
    {
      category: 'Continuous Residence Evidence',
      priority: 'High',
      suggestions: [
        'Collect utility bills spanning entire residence period',
        'Gather bank statements showing consistent US activity',
        'Include employment authorization documents',
        'Obtain lease agreements or mortgage documents'
      ]
    },
    {
      category: 'Character References',
      priority: 'Medium',
      suggestions: [
        'Request letters from US citizen employers',
        'Obtain community organization membership letters',
        'Include religious organization references',
        'Get educational institution letters if applicable'
      ]
    },
    {
      category: 'Travel Documentation',
      priority: 'High',
      suggestions: [
        'Obtain all passport pages with stamps',
        'Collect I-94 arrival/departure records',
        'Include airline tickets or itineraries',
        'Document reasons for extended trips'
      ]
    },
    {
      category: 'Criminal Record Clarification',
      priority: 'Critical',
      suggestions: [
        'Obtain certified court records for all arrests',
        'Include disposition letters for all charges',
        'Get police reports if available',
        'Include evidence of rehabilitation or community service'
      ]
    },
    {
      category: 'Military Service Documentation',
      priority: 'High',
      suggestions: [
        'Include complete DD-214 discharge papers',
        'Add service medals and commendations',
        'Include deployment records if applicable',
        'Get character references from commanding officers'
      ]
    }
  ];

  const priorityColors = {
    'Critical': 'bg-red-900/20 border-red-400 text-red-300',
    'High': 'bg-orange-900/20 border-orange-400 text-orange-300',
    'Medium': 'bg-yellow-900/20 border-yellow-400 text-yellow-300',
    'Low': 'bg-blue-900/20 border-blue-400 text-blue-300'
  };

  const getApplicableEnhancements = () => {
    const applicable = [];
    const backgroundDetails = formData.backgroundDetails || {};
    const travelHistory = formData.travelHistory || [];
    
    // Always include tax and residence evidence
    applicable.push(documentEnhancements[0]); // Tax
    applicable.push(documentEnhancements[1]); // Residence
    
    // Include character references for all cases
    applicable.push(documentEnhancements[2]); // Character
    
    // Include travel docs if there's travel history
    if (travelHistory.length > 0) {
      applicable.push(documentEnhancements[3]); // Travel
    }
    
    // Include criminal docs if there's criminal history
    if (backgroundDetails.criminalHistory && backgroundDetails.criminalHistory !== 'none') {
      applicable.push(documentEnhancements[4]); // Criminal
    }
    
    // Include military docs if military service
    if (backgroundDetails.militaryService) {
      applicable.push(documentEnhancements[5]); // Military
    }
    
    return applicable;
  };

  const handleDocumentSelection = (docType) => {
    if (selectedDocuments.includes(docType)) {
      setSelectedDocuments(selectedDocuments.filter(d => d !== docType));
    } else {
      setSelectedDocuments([...selectedDocuments, docType]);
    }
  };

  const addRecommendedDocuments = () => {
    const newDocuments = selectedDocuments.map(docType => ({
      id: Date.now() + Math.random(),
      name: `Recommended: ${docType}`,
      category: 'enhancement-recommended',
      isRecommendation: true,
      timestamp: new Date().toISOString()
    }));

    const updatedDocuments = [...(formData.documents || []), ...newDocuments];
    setFormData({ ...formData, documents: updatedDocuments, documentEnhancements: selectedDocuments });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📋 Document Quality Enhancement</h3>
        <p className="text-gray-400 text-lg">Strengthen your application with additional supporting documentation</p>
      </div>

      {/* Enhancement Overview */}
      <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-4">💡 Why Document Enhancement Matters</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h5 className="text-blue-200 font-medium mb-2">Strengthen Your Case:</h5>
            <ul className="text-blue-100 text-sm space-y-1">
              <li>• Provides additional evidence of continuous residence</li>
              <li>• Demonstrates good moral character</li>
              <li>• Shows integration into US community</li>
              <li>• Reduces chance of RFE (Request for Evidence)</li>
            </ul>
          </div>
          <div>
            <h5 className="text-blue-200 font-medium mb-2">USCIS Preferences:</h5>
            <ul className="text-blue-100 text-sm space-y-1">
              <li>• Official government documents preferred</li>
              <li>• Certified copies when originals required</li>
              <li>• Chronological organization</li>
              <li>• Clear translations for foreign documents</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Applicable Enhancement Categories */}
      <div className="space-y-4">
        <h4 className="text-xl font-semibold text-white">📂 Recommended Document Enhancements</h4>
        {getApplicableEnhancements().map((enhancement, index) => (
          <div key={index} className={`p-6 rounded-lg border ${priorityColors[enhancement.priority]}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <h5 className="text-lg font-semibold">{enhancement.category}</h5>
                <span className={`ml-3 px-2 py-1 rounded text-xs font-medium ${
                  enhancement.priority === 'Critical' ? 'bg-red-800 text-red-200' :
                  enhancement.priority === 'High' ? 'bg-orange-800 text-orange-200' :
                  enhancement.priority === 'Medium' ? 'bg-yellow-800 text-yellow-200' :
                  'bg-blue-800 text-blue-200'
                }`}>
                  {enhancement.priority} Priority
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              {enhancement.suggestions.map((suggestion, sugIndex) => (
                <div key={sugIndex} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`${enhancement.category}-${sugIndex}`}
                    checked={selectedDocuments.includes(suggestion)}
                    onChange={() => handleDocumentSelection(suggestion)}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 mr-3"
                  />
                  <label 
                    htmlFor={`${enhancement.category}-${sugIndex}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    {suggestion}
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Selected Enhancements */}
      {selectedDocuments.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">✅ Selected Enhancements ({selectedDocuments.length})</h4>
          <div className="space-y-2 mb-4">
            {selectedDocuments.map((doc, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded">
                <span className="text-gray-200 text-sm">{doc}</span>
                <button
                  onClick={() => handleDocumentSelection(doc)}
                  className="text-red-400 hover:text-red-300"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={addRecommendedDocuments}
            className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
          >
            Add Selected Documents to Collection List
          </button>
        </div>
      )}

      {/* Document Collection Strategy */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
        <h4 className="text-emerald-300 font-semibold mb-4">📋 Document Collection Strategy</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h5 className="text-emerald-200 font-medium mb-2">Priority Order:</h5>
            <ol className="text-emerald-100 text-sm space-y-1">
              <li>1. Critical items (criminal records if any)</li>
              <li>2. High priority (tax transcripts, residence proof)</li>
              <li>3. Medium priority (character references)</li>
              <li>4. Additional supporting documents</li>
            </ol>
          </div>
          <div>
            <h5 className="text-emerald-200 font-medium mb-2">Collection Tips:</h5>
            <ul className="text-emerald-100 text-sm space-y-1">
              <li>• Request official documents early (can take weeks)</li>
              <li>• Make multiple copies of everything</li>
              <li>• Organize chronologically</li>
              <li>• Translate non-English documents</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Background Review
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
        >
          Continue to Final Verification →
        </button>
      </div>
    </div>
  );
};

// US Citizenship Step 7: Final Eligibility Verification
export const CitizenshipStep7FinalVerification = ({ formData, setFormData, onNext, onBack }) => {
  const [verificationChecks, setVerificationChecks] = useState({});
  
  const eligibilityChecklist = [
    {
      category: 'Age & Status',
      items: [
        {
          id: 'age-18',
          text: 'I am at least 18 years old',
          critical: true
        },
        {
          id: 'permanent-resident',
          text: 'I am a lawful permanent resident (green card holder)',
          critical: true
        },
        {
          id: 'residence-time',
          text: 'I have been a permanent resident for the required time period',
          critical: true,
          details: '5 years (or 3 years if married to US citizen)'
        }
      ]
    },
    {
      category: 'Residence & Presence',
      items: [
        {
          id: 'continuous-residence',
          text: 'I have maintained continuous residence in the US',
          critical: true,
          details: 'No trips over 1 year that break continuous residence'
        },
        {
          id: 'physical-presence',
          text: 'I meet the physical presence requirement',
          critical: true,
          details: 'At least 30 months in US during 5-year period'
        },
        {
          id: 'state-residence',
          text: 'I have lived in my current state for at least 3 months',
          critical: true
        }
      ]
    },
    {
      category: 'Good Moral Character',
      items: [
        {
          id: 'no-serious-crimes',
          text: 'I have no disqualifying criminal convictions',
          critical: true,
          details: 'No aggravated felonies, murder, or other serious crimes'
        },
        {
          id: 'tax-compliance',
          text: 'I have filed required tax returns and paid taxes owed',
          critical: true
        },
        {
          id: 'selective-service',
          text: 'I registered for Selective Service (if required)',
          critical: false,
          details: 'Required for males who lived in US between ages 18-26'
        }
      ]
    },
    {
      category: 'English & Civics',
      items: [
        {
          id: 'english-speaking',
          text: 'I can speak English adequately',
          critical: true
        },
        {
          id: 'english-reading',
          text: 'I can read English adequately',
          critical: true
        },
        {
          id: 'english-writing',
          text: 'I can write English adequately',
          critical: true
        },
        {
          id: 'civics-knowledge',
          text: 'I have knowledge of US history and civics',
          critical: true
        }
      ]
    },
    {
      category: 'Oath Requirements',
      items: [
        {
          id: 'oath-allegiance',
          text: 'I am willing to take the Oath of Allegiance',
          critical: true
        },
        {
          id: 'bear-arms',
          text: 'I am willing to bear arms for the US (or qualify for exception)',
          critical: true
        },
        {
          id: 'civilian-service',
          text: 'I am willing to perform civilian service for the US',
          critical: true
        }
      ]
    }
  ];

  const handleCheckboxChange = (itemId, checked) => {
    setVerificationChecks({
      ...verificationChecks,
      [itemId]: checked
    });
  };

  const getCriticalRequirements = () => {
    return eligibilityChecklist.flatMap(category => 
      category.items.filter(item => item.critical)
    );
  };

  const getCheckedCritical = () => {
    const critical = getCriticalRequirements();
    return critical.filter(item => verificationChecks[item.id]);
  };

  const calculateReadiness = () => {
    const critical = getCriticalRequirements();
    const checkedCritical = getCheckedCritical();
    const percentage = critical.length > 0 ? (checkedCritical.length / critical.length) * 100 : 0;
    
    // Debug logging
    console.log('Readiness calculation:', {
      totalCritical: critical.length,
      checkedCritical: checkedCritical.length,
      percentage: percentage,
      verificationChecks: verificationChecks
    });
    
    return {
      percentage,
      ready: percentage >= 95,
      critical: critical.length,
      checked: checkedCritical.length,
      missing: critical.filter(item => !verificationChecks[item.id])
    };
  };

  const readiness = calculateReadiness();

  const handleContinue = () => {
    const finalData = {
      ...formData,
      eligibilityVerification: verificationChecks,
      readinessScore: readiness.percentage,
      finalScore: Math.round((readiness.percentage + (formData.analysisResults?.overall_score || 0)) / 2)
    };
    setFormData(finalData);
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">✅ Final Eligibility Verification</h3>
        <p className="text-gray-400 text-lg">Confirm you meet all naturalization requirements</p>
      </div>

      {/* Readiness Score */}
      <div className="bg-gradient-to-r from-blue-900/20 to-gray-900/20 p-8 rounded-lg border border-blue-400/30">
        <div className="text-center">
          <div className={`text-6xl font-bold mb-2 ${
            readiness.percentage >= 95 ? 'text-emerald-400' :
            readiness.percentage >= 80 ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {Math.round(readiness.percentage)}%
          </div>
          <div className="text-xl text-white mb-4">Naturalization Readiness</div>
          <div className={`text-lg ${
            readiness.ready ? 'text-emerald-400' : readiness.percentage >= 80 ? 'text-yellow-400' : 'text-red-400'
          }`}>
            {readiness.ready ? 'Ready to Apply' : 
             readiness.percentage >= 80 ? 'Almost Ready - Review Missing Items' :
             'Not Ready - Major Requirements Missing'}
          </div>
          <div className="text-gray-300 text-sm mt-2">
            {readiness.checked}/{readiness.critical} critical requirements met
          </div>
        </div>
      </div>

      {/* Eligibility Checklist */}
      <div className="space-y-6">
        {eligibilityChecklist.map((category, categoryIndex) => (
          <div key={categoryIndex} className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">
              {category.category}
            </h4>
            <div className="space-y-3">
              {category.items.map((item, itemIndex) => (
                <div key={itemIndex} className={`p-4 rounded-lg ${
                  item.critical ? 'bg-blue-900/20 border border-blue-500/30' : 'bg-gray-700'
                }`}>
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id={item.id}
                      checked={verificationChecks[item.id] || false}
                      onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
                      className="w-5 h-5 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 mt-1 mr-4"
                    />
                    <div className="flex-1">
                      <label htmlFor={item.id} className={`cursor-pointer text-sm ${
                        item.critical ? 'text-blue-200' : 'text-gray-200'
                      }`}>
                        {item.text}
                        {item.critical && <span className="text-red-400 ml-1">*</span>}
                      </label>
                      {item.details && (
                        <p className={`text-xs mt-1 ${
                          item.critical ? 'text-blue-300' : 'text-gray-400'
                        }`}>
                          {item.details}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Missing Requirements Warning */}
      {readiness.missing.length > 0 && (
        <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
          <h4 className="text-red-300 font-semibold mb-4">⚠️ Missing Critical Requirements</h4>
          <ul className="space-y-2">
            {readiness.missing.map((item, index) => (
              <li key={index} className="text-red-200 text-sm flex items-start">
                <span className="text-red-400 mr-2 mt-1">•</span>
                {item.text}
              </li>
            ))}
          </ul>
          <p className="text-red-200 text-sm mt-4">
            <strong>Important:</strong> You must meet all critical requirements (marked with *) before applying for naturalization.
          </p>
        </div>
      )}

      {/* Test Preparation Reminder */}
      <div className="bg-yellow-900/20 border border-yellow-400 p-6 rounded-lg">
        <h4 className="text-yellow-300 font-semibold mb-4">📚 Test Preparation Reminder</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h5 className="text-yellow-200 font-medium mb-2">English Test:</h5>
            <ul className="text-yellow-100 text-sm space-y-1">
              <li>• Speaking (during interview)</li>
              <li>• Reading (1-3 sentences)</li>
              <li>• Writing (1-3 sentences)</li>
            </ul>
          </div>
          <div>
            <h5 className="text-yellow-200 font-medium mb-2">Civics Test:</h5>
            <ul className="text-yellow-100 text-sm space-y-1">
              <li>• Up to 10 questions asked</li>
              <li>• Must answer 6 correctly</li>
              <li>• US history and government</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Legal Disclaimer */}
      <div className="bg-gray-800 border border-gray-600 p-4 rounded-lg">
        <h5 className="text-gray-300 font-medium mb-2">📋 Legal Disclaimer</h5>
        <p className="text-gray-400 text-xs">
          This verification checklist is for informational purposes only and does not constitute legal advice. 
          Immigration law is complex and individual circumstances vary. Consider consulting with a qualified 
          immigration attorney for personalized guidance about your naturalization case.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Document Enhancement
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
        >
          Generate Naturalization Package →
        </button>
      </div>
    </div>
  );
};

// US Citizenship Step 8: Generate Naturalization Package
export const CitizenshipStep8GeneratePackage = ({ formData, setFormData, onNext, onBack, onClose }) => {
  const [generating, setGenerating] = useState(false);
  const [packageResult, setPackageResult] = useState(formData.package || null);
  const [downloading, setDownloading] = useState(false);

  const generatePackage = async () => {
    setGenerating(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ai/generate-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_type: 'N-400 Naturalization Application Package',
          petitioner_details: {
            fullName: formData.backgroundDetails?.fullName || "Naturalization Applicant",
            status: formData.currentStatus || "green-card-holder",
            eligibilityPath: formData.eligibilityPath || "lawful-permanent-resident",
            yearsInUS: formData.residenceDetails?.yearsInUS || "5-plus",
            currentState: formData.residenceDetails?.currentState || "Various States"
          },
          beneficiary_details: {
            fullName: formData.backgroundDetails?.fullName || "Naturalization Applicant",
            status: "naturalization-applicant",
            country: "United States",
            location: formData.residenceDetails?.currentState || "United States"
          },
          relationship_details: {
            eligibilityPath: formData.eligibilityPath || "",
            criminalHistory: formData.backgroundDetails?.criminalHistory || "none",
            taxCompliance: formData.backgroundDetails?.taxCompliance || "current",
            militaryService: formData.backgroundDetails?.militaryService || false,
            travelHistory: (formData.travelHistory || []).length,
            physicalPresence: formData.analysisResults?.travel_analysis?.meets_physical_presence || true,
            continuousResidence: formData.analysisResults?.travel_analysis?.continuous_residence || "Maintained",
            readinessScore: formData.readinessScore || 0
          },
          documents: (formData.documents || []).map(doc => {
            // Create a copy without the file object for JSON serialization
            const { file, ...docWithoutFile } = doc;
            return docWithoutFile;
          }),
          analysis_results: formData.analysisResults || {},
          final_score: formData.finalScore || 80
        })
      });

      if (response.ok) {
        const result = await response.json();
        setPackageResult(result);
        setFormData({ ...formData, package: result });
      } else {
        console.error('Package generation failed:', response.status);
        // Fallback to dynamic package generation
        const fallbackPackage = generateFallbackPackage(formData);
        setPackageResult(fallbackPackage);
        setFormData({ ...formData, package: fallbackPackage });
      }
    } catch (error) {
      console.error('Package generation error:', error);
      // Fallback to dynamic package generation
      const fallbackPackage = generateFallbackPackage(formData);
      setPackageResult(fallbackPackage);
      setFormData({ ...formData, package: fallbackPackage });
    }
    
    setGenerating(false);
  };

  const generateFallbackPackage = (data) => {
    const packageId = `NATURAL-${Date.now()}`;
    const applicantName = data.backgroundDetails?.fullName || "Naturalization Applicant";
    const eligibilityPath = data.eligibilityPath || "lawful-permanent-resident";
    
    return {
      package_id: packageId,
      package_type: "N-400 Naturalization Application Package",
      applicant_name: applicantName,
      generation_date: new Date().toISOString(),
      total_documents: 8,
      estimated_review_time: "45-60 minutes",
      package_contents: [
        'N-400_Application_Form_Guide.docx',
        'Naturalization_Eligibility_Checklist.docx',
        'Document_Collection_Checklist.docx',
        'Travel_History_Worksheet.docx',
        'Civics_Test_Study_Guide.docx',
        'Interview_Preparation_Guide.docx',
        'Application_Timeline_and_Process.docx',
        'Character_Reference_Templates.docx'
      ],
      special_instructions: [
        `Eligibility pathway: ${data.pathDetails?.title || 'Lawful Permanent Resident'}`,
        `Application strength: ${data.finalScore || 80}%`,
        data.analysisResults?.travel_analysis?.meets_physical_presence ? 
          'Physical presence requirement met' : 
          'Review physical presence calculation',
        data.backgroundDetails?.criminalHistory === 'none' ? 
          'No criminal background concerns' : 
          'Include criminal record documentation',
        `Document enhancement suggestions: ${(data.documentEnhancements || []).length} items`
      ]
    };
  };

  const downloadPackage = async () => {
    if (!packageResult) return;
    
    // Check if this is a fallback package (local generation)
    if (packageResult.package_id.startsWith('NATURAL-')) {
      alert('This package was generated locally. Please regenerate the package to create a downloadable version.');
      return;
    }
    
    setDownloading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/download-package/${packageResult.package_id}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Naturalization_Package_${packageResult.package_id}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        if (response.status === 404) {
          alert('Package not found. Please regenerate the package and try again.');
        } else {
          alert('Download failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Download failed. Please check your connection and try again.');
    }
    setDownloading(false);
  };

  React.useEffect(() => {
    if (!packageResult) {
      generatePackage();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📦 Generate Naturalization Package</h3>
        <p className="text-gray-400 text-lg">Complete application package with forms, guides, and checklists</p>
      </div>

      {generating && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Your Naturalization Package</h4>
          <p className="text-gray-400">Creating comprehensive application materials...</p>
        </div>
      )}

      {!generating && packageResult && (
        <>
          {/* Package Overview */}
          <div className="bg-gradient-to-r from-emerald-900/20 to-blue-900/20 p-8 rounded-lg border border-emerald-400/30">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">📋</div>
              <h4 className="text-2xl font-bold text-white mb-2">Naturalization Package Ready!</h4>
              <p className="text-emerald-300">Package ID: {packageResult.package_id}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-400">{packageResult.total_documents}</div>
                <div className="text-gray-300 text-sm">Documents Included</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400">{formData.finalScore || 80}%</div>
                <div className="text-gray-300 text-sm">Application Strength</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-400">{packageResult.estimated_review_time}</div>
                <div className="text-gray-300 text-sm">Review Time</div>
              </div>
            </div>
          </div>

          {/* Package Contents */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📋 Package Contents</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {packageResult.package_contents?.map((doc, index) => (
                <div key={index} className="flex items-center p-3 bg-gray-700 rounded">
                  <span className="text-2xl mr-3">📄</span>
                  <span className="text-gray-200 text-sm">{doc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Special Instructions */}
          <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
            <h4 className="text-blue-300 font-semibold mb-4">⭐ Special Instructions for Your Case</h4>
            <ul className="space-y-2">
              {packageResult.special_instructions?.map((instruction, index) => (
                <li key={index} className="text-blue-200 text-sm flex items-start">
                  <span className="text-blue-400 mr-2 mt-1">•</span>
                  {instruction}
                </li>
              ))}
            </ul>
          </div>

          {/* Application Summary */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📊 Application Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="text-gray-300 font-medium mb-3">Eligibility Path:</h5>
                <p className="text-blue-400 mb-4">{formData.pathDetails?.title || 'Lawful Permanent Resident'}</p>
                
                <h5 className="text-gray-300 font-medium mb-3">Documents Collected:</h5>
                <p className="text-blue-400 mb-4">{(formData.documents || []).length} documents uploaded</p>
                
                <h5 className="text-gray-300 font-medium mb-3">Travel History:</h5>
                <p className="text-blue-400">{(formData.travelHistory || []).length} trips recorded</p>
              </div>
              <div>
                <h5 className="text-gray-300 font-medium mb-3">Background Status:</h5>
                <p className={`mb-4 ${
                  formData.backgroundDetails?.criminalHistory === 'none' ? 'text-emerald-400' : 'text-yellow-400'
                }`}>
                  {formData.backgroundDetails?.criminalHistory === 'none' ? 'Clean background' : 'Background reviewed'}
                </p>
                
                <h5 className="text-gray-300 font-medium mb-3">Physical Presence:</h5>
                <p className={`mb-4 ${
                  formData.analysisResults?.travel_analysis?.meets_physical_presence ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {formData.analysisResults?.travel_analysis?.meets_physical_presence ? 'Requirement met' : 'Needs review'}
                </p>
                
                <h5 className="text-gray-300 font-medium mb-3">Readiness Score:</h5>
                <p className={`${
                  (formData.readinessScore || 0) >= 95 ? 'text-emerald-400' : 
                  (formData.readinessScore || 0) >= 80 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {formData.readinessScore || 0}% ready
                </p>
              </div>
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg text-center">
            <h4 className="text-emerald-300 font-semibold mb-4">📥 Download Your Package</h4>
            <p className="text-emerald-200 text-sm mb-6">
              Your complete naturalization package includes forms, guides, checklists, and personalized instructions in Microsoft Word format.
            </p>
            
            {packageResult?.package_id.startsWith('NATURAL-') ? (
              // Fallback package - show regenerate option
              <div className="space-y-4">
                <div className="bg-yellow-900/20 border border-yellow-400 p-4 rounded-lg mb-4">
                  <p className="text-yellow-200 text-sm">
                    This package was generated locally. To download a complete Word document package, please regenerate it.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setPackageResult(null);
                    setFormData({ ...formData, package: null });
                    generatePackage();
                  }}
                  disabled={generating}
                  className={`px-8 py-4 rounded-lg font-semibold text-lg transition-colors ${
                    generating
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {generating ? (
                    <>
                      <span className="animate-spin inline-block mr-2">⏳</span>
                      Regenerating Package...
                    </>
                  ) : (
                    <>
                      🔄 Regenerate Downloadable Package
                    </>
                  )}
                </button>
              </div>
            ) : (
              // Real backend package - show download
              <button
                onClick={downloadPackage}
                disabled={downloading}
                className={`px-8 py-4 rounded-lg font-semibold text-lg transition-colors ${
                  downloading
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-emerald-500 text-white hover:bg-emerald-600'
                }`}
              >
                {downloading ? (
                  <>
                    <span className="animate-spin inline-block mr-2">⏳</span>
                    Preparing Download...
                  </>
                ) : (
                  <>
                    📥 Download Package (.zip)
                  </>
                )}
              </button>
            )}
          </div>

          {/* Next Steps */}
          <div className="bg-yellow-900/20 border border-yellow-400 p-6 rounded-lg">
            <h4 className="text-yellow-300 font-semibold mb-4">🎯 Next Steps</h4>
            <ol className="text-yellow-200 text-sm space-y-2">
              <li>1. Review all documents in your package thoroughly</li>
              <li>2. Complete the N-400 application form using the provided guide</li>
              <li>3. Gather any missing documents identified in the checklist</li>
              <li>4. Study for the English and civics tests using included materials</li>
              <li>5. Submit your application with required fees</li>
              <li>6. Prepare for your naturalization interview</li>
            </ol>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={generating}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            generating
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gray-700 text-white hover:bg-gray-600'
          }`}
        >
          ← Back to Final Verification
        </button>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-emerald-500 text-white hover:bg-emerald-600"
        >
          Complete & Close
        </button>
      </div>
    </div>
  );
};